import { StoredQuote } from './quoteRecord';

export interface JourneyPayment {
  lockedMinor?: number;
  outstandingMinor?: number;
  receivedMinor?: number;
  currentlyDueMinor?: number;
  depositDueMinor?: number;
  depositExpectedMinor?: number;
  depositReceivedMinor?: number;
}

export interface JourneyBooking {
  shootDate?: string;
  startTime?: string;
  endTime?: string;
  locationName?: string;
  dateConfirmed?: boolean;
  proposedShootDate?: string;
  proposedStartTime?: string;
  completedAt?: string;
  /** Promised date for the edited photographs (YYYY-MM-DD). Set when the shoot is marked done. */
  deliveryDueOn?: string;
  deliveredAt?: string;
  deliveryLink?: string;
  closedAt?: string;
  /** Multi-day coverage; the job stays booked until the last session date has passed. */
  sessions?: Array<{ shootDate?: string; startTime?: string; endTime?: string; label?: string }>;
}

/**
 * Where a job sits in the studio lifecycle. One value per job so lists, the
 * pipeline and the calendar agree. `lost` covers declined, archived and deleted.
 */
export type JourneyStage =
  | 'enquiry'
  | 'quoted'
  | 'accepted'
  | 'booked'
  | 'editing'
  | 'delivered'
  | 'closed'
  | 'lost';

export const STAGE_ORDER: JourneyStage[] = ['enquiry', 'quoted', 'accepted', 'booked', 'editing', 'delivered', 'closed'];

export const STAGE_TITLES: Record<JourneyStage, string> = {
  enquiry: 'Client details',
  quoted: 'Quoted',
  accepted: 'Accepted',
  booked: 'Booked',
  editing: 'Editing',
  delivered: 'Delivered',
  closed: 'Closed',
  lost: 'Lost',
};

/** Today in the studio's timezone as YYYY-MM-DD. */
export function studioToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/** Whole days from `from` to `to` (both YYYY-MM-DD); negative when `to` is in the past. */
export function daysUntil(to: string | undefined, from: string): number | null {
  if (!to || !/^\d{4}-\d{2}-\d{2}$/.test(to) || !/^\d{4}-\d{2}-\d{2}$/.test(from)) return null;
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.round((b - a) / 86400000);
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function shortDay(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  return `${Number(match[3])} ${SHORT_MONTHS[Number(match[2]) - 1] || match[2]}`;
}

/** "4 days until the client's delivery date (14 Nov)" — shared by the job file, Today and reminders. */
export function deliveryCountdown(dueOn: string | undefined, today: string = studioToday()): { daysLeft: number | null; text: string; overdue: boolean } {
  const daysLeft = daysUntil(dueOn, today);
  if (daysLeft === null || !dueOn) return { daysLeft: null, text: 'No delivery date set', overdue: false };
  const day = shortDay(dueOn);
  if (daysLeft > 1) return { daysLeft, text: `${daysLeft} days until the client's delivery date (${day})`, overdue: false };
  if (daysLeft === 1) return { daysLeft, text: `1 day until the client's delivery date (${day})`, overdue: false };
  if (daysLeft === 0) return { daysLeft, text: `Delivery date is today (${day})`, overdue: false };
  if (daysLeft === -1) return { daysLeft, text: `Delivery date was yesterday (${day})`, overdue: true };
  return { daysLeft, text: `${Math.abs(daysLeft)} days past the client's delivery date (${day})`, overdue: true };
}

export interface JourneyEnquiry {
  id?: string;
  source?: string;
  shootType?: string;
  notes?: string;
  receivedAt?: string;
}

/** Where the "next step" button should take the studio. */
export type JourneyTab = 'builder' | 'conversation' | 'booking' | 'money' | null;

export interface JourneyView {
  /** Lifecycle stage; drives pipeline columns, list filters and the calendar. */
  stage: JourneyStage;
  journeyStatus: string;
  nextAction: string;
  /** Short label for the single button that does the next step. */
  nextButton: string;
  /** Which screen that button opens. `null` means nothing to do right now. */
  nextTab: JourneyTab;
  /** True when Carmen has something to do (as opposed to waiting on the client or finished). */
  needsAction: boolean;
  /** A balance is still open. Shown beside the production step; it does not replace it. */
  paymentOpen: boolean;
  financiallySecured: boolean;
  dateProposed: boolean;
  dateConfirmed: boolean;
  moneyLine: string;
  bookingLine: string;
  /** Editing jobs: "4 days until the client's delivery date (14 Nov)". Empty otherwise. */
  deliveryLine: string;
  /** Editing jobs: days to the delivery date (negative when overdue). */
  daysToDelivery: number | null;
  deliveryOverdue: boolean;
}

function money(minor?: number) {
  const value = Math.max(0, Math.round(Number(minor) || 0));
  const whole = Math.floor(value / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const cents = String(value % 100).padStart(2, '0');
  return `R ${whole}.${cents}`;
}

export function isFinanciallySecured(summary?: JourneyPayment | null) {
  if (!summary) return false;
  const depositExpected = Math.max(0, Math.round(Number(summary.depositExpectedMinor ?? summary.depositDueMinor) || 0));
  const depositReceived = Math.max(0, Math.round(Number(summary.depositReceivedMinor) || 0));
  const received = Math.max(0, Math.round(Number(summary.receivedMinor) || 0));
  const outstanding = Math.max(0, Math.round(Number(summary.outstandingMinor) || 0));
  if (depositExpected > 0 && depositReceived >= depositExpected) return true;
  if (outstanding === 0 && received > 0) return true;
  return false;
}

/** The quotation that represents a client right now: accepted, then issued, then draft. */
export function pickCurrentQuote<T extends { id?: string; status?: string; updatedAt?: string; deletedAt?: string | null }>(
  quotes: T[],
): T | null {
  const live = quotes.filter((quote) => !quote.deletedAt);
  const ranked = [...live].sort((left, right) => String(right.updatedAt || '').localeCompare(String(left.updatedAt || '')));
  return ranked.find((quote) => quote.status === 'accepted')
    || ranked.find((quote) => quote.status === 'issued')
    || ranked.find((quote) => quote.status === 'draft')
    || ranked[0]
    || quotes[0]
    || null;
}

/**
 * Single authoritative journey display for Overview and money panels.
 * Display states only — does not invent payment or booking facts.
 */
export function deriveClientJourney(
  quote: StoredQuote | null | undefined,
  summary?: JourneyPayment | null,
  booking?: JourneyBooking | null,
  enquiry?: JourneyEnquiry | null,
  today: string = studioToday(),
): JourneyView {
  const secured = isFinanciallySecured(summary);
  const dateConfirmed = Boolean(booking?.dateConfirmed && booking?.shootDate);
  const lastShootDate = (() => {
    const dates = (booking?.sessions || [])
      .map((session) => String(session.shootDate || ''))
      .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
      .sort();
    return dates[dates.length - 1] || booking?.shootDate || '';
  })();
  const shootPassed = Boolean(dateConfirmed && lastShootDate && lastShootDate < today && !booking?.completedAt);
  const countdown = booking?.completedAt && !booking?.deliveredAt
    ? deliveryCountdown(booking.deliveryDueOn, today)
    : { daysLeft: null, text: '', overdue: false };
  const dateProposed = Boolean(
    (!dateConfirmed && booking?.shootDate)
    || booking?.proposedShootDate,
  );
  const preferredReady = Boolean(quote?.preferredDate && quote?.preferredTime);
  const savedReady = Boolean(
    (booking?.shootDate && booking?.startTime)
    || (booking?.proposedShootDate && booking?.proposedStartTime),
  );
  const readyToConfirm = quote?.status === 'accepted' && !dateConfirmed && (savedReady || preferredReady);
  const changeWaiting = Boolean(dateConfirmed && booking?.proposedShootDate && booking?.proposedStartTime);
  const outstanding = Math.max(0, Math.round(Number(summary?.outstandingMinor) || 0));
  const received = Math.max(0, Math.round(Number(summary?.receivedMinor) || 0));
  const depositDue = Math.max(0, Math.round(Number(summary?.depositDueMinor ?? summary?.depositExpectedMinor) || 0));

  let stage: JourneyStage = 'enquiry';
  let journeyStatus = 'Client details';
  let nextAction = 'Prepare a quotation';
  let nextButton = 'Prepare quotation';
  let nextTab: JourneyTab = 'builder';
  let needsAction = true;

  if (!quote) {
    return {
      stage: 'enquiry',
      journeyStatus: 'Client details',
      nextAction: enquiry ? 'Prepare a quotation' : 'Add client details or prepare a quotation',
      nextButton: 'Prepare quotation',
      nextTab: 'builder',
      needsAction: true,
      paymentOpen: false,
      financiallySecured: false,
      dateProposed: false,
      dateConfirmed: false,
      moneyLine: 'No quotation yet',
      bookingLine: 'No date arranged',
      deliveryLine: '',
      daysToDelivery: null,
      deliveryOverdue: false,
    };
  }

  if (quote.deletedAt) {
    stage = 'lost';
    journeyStatus = 'Recently deleted';
    nextAction = 'Restore or leave archived history';
    nextButton = '';
    nextTab = null;
    needsAction = false;
  } else if (quote.archivedAt) {
    stage = 'lost';
    journeyStatus = 'Archived';
    nextAction = 'Reopen from archive if needed';
    nextButton = '';
    nextTab = null;
    needsAction = false;
  } else if (booking?.closedAt) {
    stage = 'closed';
    journeyStatus = 'Closed';
    nextAction = outstanding > 0
      ? 'Closed with a balance still showing. Reopen if that needs attention.'
      : 'The job is finished. Nothing to do.';
    nextButton = '';
    nextTab = null;
    needsAction = false;
    } else if (booking?.deliveredAt) {
    stage = 'delivered';
    nextButton = 'Close job';
    nextTab = 'money';
    needsAction = true;
    if (outstanding <= 0 && received > 0) {
      journeyStatus = 'Delivered and paid in full';
      nextAction = 'The photographs are with the client and the money is in. Close the job when you are done. Change the status if it needs to move back.';
    } else if (outstanding > 0) {
      journeyStatus = 'Delivered · final balance outstanding';
      nextAction = 'The photographs went out. Close the job when you are ready. Record the balance beside this — payment does not block the next step.';
    } else {
      journeyStatus = 'Delivered';
      nextAction = 'Close the job when you are done with it. Change the status if it needs to move back.';
    }
  } else if (booking?.completedAt) {
    // Shoot done, photographs not yet delivered: the client is waiting.
    stage = 'editing';
    journeyStatus = countdown.overdue ? 'Editing · delivery overdue' : 'Editing · client waiting';
    const moneyHint = outstanding > 0 ? ` ${money(outstanding)} is still outstanding.` : '';
    nextAction = countdown.daysLeft === null
      ? `Edit and deliver the photographs. Add the WeTransfer link when you hand them over.${moneyHint}`
      : `${countdown.text}. Add the WeTransfer link when you hand them over.${moneyHint}`;
    nextButton = 'Deliver photos';
    nextTab = 'money';
    needsAction = true;
  } else if (changeWaiting) {
    stage = 'booked';
    journeyStatus = 'Date change to confirm';
    nextAction = 'Confirm the new date, or change the details first. The client is emailed when you confirm.';
    nextButton = 'Confirm shoot';
    nextTab = 'booking';
    needsAction = true;
  } else if (dateConfirmed) {
    stage = 'booked';
    const confirmedOn = shortDay(String(booking?.shootDate || ''));
    nextButton = 'Finish shoot';
    nextTab = 'booking';
    if (shootPassed) {
      journeyStatus = secured ? 'Shoot date passed' : 'Shoot date passed · payment still open';
      nextAction = 'The shoot date has passed. Finish the shoot to start editing. Change the status if this job is already further along.';
      needsAction = true;
    } else if (outstanding <= 0 && received > 0) {
      journeyStatus = 'Paid in full · shoot booked';
      nextAction = confirmedOn
        ? `Confirmed for ${confirmedOn}. Finish the shoot after the day, or finish it now if the day is already done.`
        : 'Finish the shoot when the day is done.';
      needsAction = false;
    } else if (!secured) {
      journeyStatus = 'Date confirmed · payment still open';
      nextAction = confirmedOn
        ? `Confirmed for ${confirmedOn}. Finish the shoot after the day, or now if the day is already done. Payment is still open.`
        : 'Finish the shoot when the day is done. Payment is still open.';
      needsAction = false;
    } else {
      journeyStatus = 'Date confirmed';
      nextAction = confirmedOn
        ? `Confirmed for ${confirmedOn}. Finish the shoot after the day, or now if the day is already done.`
        : 'Finish the shoot when the day is done.';
      needsAction = false;
    }
  } else if (readyToConfirm) {
    stage = 'accepted';
    journeyStatus = 'Date to confirm';
    nextAction = secured
      ? 'The deposit is in. Confirm the booking. The client is waiting for confirmation, and is emailed when you confirm.'
      : 'Confirm the shoot, or change the date, time, or place first. The client is emailed when you confirm.';
    nextButton = 'Confirm shoot';
    nextTab = 'booking';
  } else if (dateProposed) {
    stage = 'accepted';
    journeyStatus = 'Date to confirm';
    nextAction = secured
      ? 'The deposit is in. Add a start time, then confirm the booking. The client is waiting for confirmation.'
      : 'Add a start time, then confirm the shoot.';
    nextButton = 'Set shoot date';
    nextTab = 'booking';
  } else if (quote.status === 'accepted' && secured) {
    stage = 'accepted';
    journeyStatus = 'Financially secured, date to arrange';
    nextAction = 'The deposit is in. Agree a date and confirm the booking. The client is waiting for confirmation.';
    nextButton = 'Set shoot date';
    nextTab = 'booking';
  } else if (quote.status === 'accepted' && quote.acceptanceChannel === 'email') {
    stage = 'accepted';
    journeyStatus = 'Accepted from email, date to confirm';
    nextAction = 'The total is locked from their email. No payment was recorded. Add a date and start time, then confirm the shoot.';
    nextButton = 'Set shoot date';
    nextTab = 'booking';
  } else if (quote.status === 'accepted') {
    stage = 'accepted';
    journeyStatus = 'Accepted, deposit outstanding';
    nextAction = depositDue > 0
      ? 'Waiting for the deposit. Record it when it arrives. You can still set the date and confirm the shoot.'
      : 'Set the date and confirm the shoot. Record a payment when it arrives.';
    nextButton = 'Set shoot date';
    nextTab = 'booking';
  } else if (quote.status === 'issued') {
    stage = 'quoted';
    journeyStatus = 'Awaiting client response';
    nextAction = 'Waiting for the client. Mark accepted on Quoted if they replied by email.';
    nextButton = 'Open conversation';
    nextTab = 'conversation';
    needsAction = false;
  } else if (quote.status === 'draft') {
    stage = 'enquiry';
    journeyStatus = 'Draft quotation';
    nextAction = 'Finish the quotation and issue it to the client';
    nextButton = 'Open quotation';
    nextTab = 'money';
  } else if (quote.status === 'declined') {
    stage = 'lost';
    journeyStatus = 'Declined';
    nextAction = 'Archive or revise if they return';
    nextButton = '';
    nextTab = null;
    needsAction = false;
  } else {
    stage = 'lost';
    journeyStatus = quote.status;
    nextAction = 'Review this quotation';
    nextButton = '';
    nextTab = null;
    needsAction = false;
  }

  const moneyLine = summary
    ? `Received ${money(received)} · Outstanding ${money(outstanding)}${secured ? ' · Financially secured' : ''}`
    : `Quoted total ${money(quote.snapshot?.quoteTotalMinor)}`;

  const when = dateConfirmed
    ? `${booking?.shootDate}${booking?.startTime ? ` · ${booking.startTime}` : ''}${booking?.endTime ? `–${booking.endTime}` : ''}`
    : booking?.proposedShootDate
      ? `Proposed ${booking.proposedShootDate}${booking?.proposedStartTime ? ` · ${booking.proposedStartTime}` : ''}`
      : booking?.shootDate
        ? `Proposed ${booking.shootDate}${booking?.startTime ? ` · ${booking.startTime}` : ''}`
        : 'No date arranged';

  const bookingLine = booking?.locationName ? `${when} · ${booking.locationName}` : when;

  return {
    stage,
    journeyStatus,
    nextAction,
    nextButton,
    nextTab,
    needsAction,
    paymentOpen: outstanding > 0 && !quote.deletedAt && !quote.archivedAt && (quote.status === 'accepted' || quote.status === 'issued'),
    financiallySecured: secured,
    dateProposed,
    dateConfirmed,
    moneyLine,
    bookingLine,
    deliveryLine: stage === 'editing' ? countdown.text : '',
    daysToDelivery: stage === 'editing' ? countdown.daysLeft : null,
    deliveryOverdue: stage === 'editing' && countdown.overdue,
  };
}
